// ** Third Party Components 
import { MoreHorizontal } from "lucide-react"
import { Button } from "@components/common/button"
import { Popover, PopoverContent, PopoverTrigger } from "@components/common/popover"

// ** Types
import { DictType } from "@pma/types"

// ** Helpers
import { cn } from "@pma/utils/helpers"

// ** Custom Hooks
import { useFilesView } from "@pma/hooks/use-files-view"

// ** Custom Components
import DeleteFolder from "../../delete-folder"
import AddSubFolder from "../../add-sub-folder"
import RestoreFolder from "../../restore-folder"

interface FolderContextMenuProps {
  item: DictType
  isActive: boolean
}

export default function FolderContextMenu({ item, isActive }: FolderContextMenuProps) {
  const {
    rootFolder,
    isOrganizationOwner,
    isAdmin,
    folderItems,
    foldersMeta,
    breadcrumbs,
    setFolderItems,
    setFoldersMeta,
    setBreadcrumbs,
    setIsShow,
    setActiveFolder,
    setRecentDeletedFolder,
    handleTreeItemFolderClick,
    refetch,
    refetchAllFolder,
  } = useFilesView()

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 p-0 hover:bg-white"
          onClick={(e) => e.stopPropagation()}
        >
          <MoreHorizontal
            className={cn("h-4 w-4", isActive ? "text-white" : "text-gray-500")}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-fit p-2">
        {item.deleted && (isOrganizationOwner || isAdmin || item.deleted_by === (window as any).__authConfig?.id) ? (
          <div className="flex flex-col gap-2" onClick={(e) => e.stopPropagation()}>
            <RestoreFolder
              folder={item}
              setFolderItems={setFolderItems}
              isMenuView={true}
              revalidate={refetch}
              refetchAllFolder={refetchAllFolder}
              setActiveFolder={setActiveFolder}
            />
          </div>
        ) : !item.deleted ? (
          <div className="flex flex-col gap-2">
            {/* Add subfolder */}
            <button
              className="flex items-center gap-2 rounded p-1 hover:bg-muted"
              onClick={(e) => e.stopPropagation()}
            >
              <AddSubFolder
                parent={item}
                revalidate={refetch}
                isMenuView={true}
                type={0}
                refetchAllFolder={refetchAllFolder}
              />
            </button>

            {/* Delete folder (not root) */}
            {item.id !== rootFolder.id && (
              <button
                className="flex items-center gap-2 rounded p-1 hover:bg-muted"
                onClick={(e) => e.stopPropagation()}
              >
                <DeleteFolder
                  folder={item}
                  isMenuView={true}
                  setFolderItems={setFolderItems}
                  foldersMeta={foldersMeta}
                  setIsShow={setIsShow}
                  breadcrumbs={breadcrumbs}
                  setBreadcrumbs={setBreadcrumbs}
                  handleTreeItemFolderClick={handleTreeItemFolderClick}
                  setFoldersMeta={setFoldersMeta}
                  setRecentDeletedFolder={setRecentDeletedFolder}
                  refetchAllFolder={refetchAllFolder}
                />
              </button>
            )}

            {/* Rename folder (non-root, level > 0) */}
            {item.level > 0 && (
              <button
                className="flex items-center gap-2 rounded p-1 hover:bg-muted"
                onClick={(e) => e.stopPropagation()}
              >
                <AddSubFolder
                  parent={item}
                  revalidate={refetch}
                  isMenuView={true}
                  type={1}
                  folderItems={folderItems}
                  setFolderItems={setFolderItems}
                  setActiveFolder={setActiveFolder}
                  setBreadcrumbs={setBreadcrumbs}
                />
              </button>
            )}
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  )
}